import React from "react";
import { useLocation } from "react-router-dom";
import ErrorFallback from "./ErrorFallback";

function resetKeysChanged(prev, next) {
  if (prev === next) return false;
  if (!prev || !next) return true;
  if (prev.length !== next.length) return true;
  return prev.some((value, index) => value !== next[index]);
}

/**
 * Catches render errors in the child tree and shows a fallback UI.
 * Pass `resetKeys` (e.g. [pathname]) to clear the error when they change.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep console signal for ops / local debugging; no external reporter wired yet.
    console.error("[ErrorBoundary]", error, errorInfo?.componentStack);
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, errorInfo);
      } catch (_) {
        /* ignore reporter failures */
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === "function") {
      this.props.onReset();
    }
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback, variant = "page" } = this.props;

    if (!hasError) {
      return children;
    }

    if (typeof fallback === "function") {
      return fallback({
        error,
        resetErrorBoundary: this.resetErrorBoundary,
        variant,
      });
    }

    if (fallback != null) {
      return fallback;
    }

    return (
      <ErrorFallback
        error={error}
        resetErrorBoundary={this.resetErrorBoundary}
        variant={variant}
      />
    );
  }
}

/**
 * Error boundary that auto-resets when the route pathname changes.
 * Must be used inside a React Router tree.
 */
export function RouteErrorBoundary({ children, variant = "page", ...rest }) {
  const location = useLocation();
  return (
    <ErrorBoundary
      variant={variant}
      resetKeys={[location.pathname, location.search]}
      {...rest}
    >
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
